import React, { useRef, useState } from 'react';
// Appel public dédié : aucun jeton de connexion n'est nécessaire pour contacter le support.
import { sendContactMessage } from '../services/contactService';
import { Form, Input, Button } from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  MessageOutlined,
  UserOutlined,
  TeamOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
// Thème citron / noir / blanc de la page (classes préfixées "ct-")
import './Contact.css';

const { TextArea } = Input;

// Coordonnées affichées dans la colonne de droite (données séparées du rendu)
const COORDONNEES = [
  { icon: <MailOutlined />, label: 'Email', texte: 'shopmercuri@gmail.com', href: 'mailto:shopmercuri@gmail.com' },
  { icon: <PhoneOutlined />, label: 'Téléphone', texte: '+225 07 59 95 47 08', href: 'tel:+2250759954708' },
  { icon: <EnvironmentOutlined />, label: 'Adresse', texte: "Abidjan, Côte d'Ivoire" }
];

// Heures d'ouverture
const HORAIRES = [
  { jour: 'Lundi - Vendredi', heures: '08:00 - 18:00' },
  { jour: 'Samedi', heures: '09:00 - 13:00' },
  { jour: 'Dimanche', heures: 'Fermé' }
];

// Questions fréquentes
const FAQ = [
  {
    icon: <MessageOutlined />,
    question: 'Comment puis-je démarrer avec Mercuri Shop ?',
    reponse: "Pour commencer, inscrivez-vous à l'essai gratuit de 14 jours. Vous pourrez connecter votre numéro WhatsApp et ajouter vos premiers produits en quelques minutes."
  },
  {
    icon: <TeamOutlined />,
    question: 'Proposez-vous une démonstration ?',
    reponse: 'Oui, nous proposons des démonstrations personnalisées pour les entreprises qui souhaitent explorer Mercuri Shop en profondeur. Contactez notre équipe commerciale pour planifier une session.'
  },
  {
    icon: <UserOutlined />,
    question: 'Puis-je migrer depuis une autre solution ?',
    reponse: 'Absolument ! Notre équipe vous accompagne dans la migration depuis d\'autres plateformes. Nous assurons une transition en douceur sans perturber vos communications clients.'
  }
];

/** Affiche les coordonnées et transmet les demandes au support en conservant la saisie en cas d'échec. */
const Contact = () => {
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Empêche deux soumissions rapprochées, y compris avant le prochain rendu React.
  const requestPending = useRef(false);

  /** Attend la confirmation serveur avant de vider le formulaire et affiche les erreurs sans perdre le message. */
  const onFinish = async (values) => {
    if (requestPending.current) return;
    requestPending.current = true;
    setSending(true);
    setFeedback(null);
    try {
      await sendContactMessage(values);
      form.resetFields();
      setFeedback({ success: true, text: 'Votre message a été transmis à notre équipe.' });
    } catch (error) {
      setFeedback({ success: false, text: error.response?.status === 429
        ? 'Trop de demandes. Veuillez réessayer dans quinze minutes.'
        : 'Impossible d’envoyer votre message. Votre saisie est conservée. Réessayez ou écrivez à shopmercuri@gmail.com.' });
    } finally {
      requestPending.current = false;
      setSending(false);
    }
  };

  return (
    <div className="ct-page">
      {/* Hero */}
      <section className="ct-hero">
        <span className="ct-pill">Contact</span>
        <h1>Parlons de votre <span className="ct-mark">projet</span></h1>
        <p>
          Notre équipe est à votre disposition pour répondre à toutes vos questions
          et vous accompagner dans votre projet.
        </p>
      </section>

      {/* Formulaire et coordonnées */}
      <section className="ct-main">
        <div className="ct-card ct-form-card">
          <h2>Envoyez-nous un message</h2>
          {/* Le résultat est annoncé aux lecteurs d'écran et reste visible après la soumission. */}
          {feedback && <div className={`ct-feedback ${feedback.success ? 'ct-feedback-success' : 'ct-feedback-error'}`} role={feedback.success ? 'status' : 'alert'}>{feedback.text}</div>}
          <Form
            form={form}
            name="contact"
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            className="ct-form"
            disabled={sending}
            aria-busy={sending}
          >
            {/* Champ leurre masqué : les visiteurs et lecteurs d'écran n'ont pas à le remplir. */}
            <div className="ct-honeypot" aria-hidden="true">
              <Form.Item name="website"><Input tabIndex={-1} autoComplete="off" /></Form.Item>
            </div>
            <div className="ct-row">
              <Form.Item
                name="name"
                label="Nom complet"
                rules={[{ required: true, message: 'Veuillez saisir votre nom' }]}
              >
                <Input prefix={<UserOutlined />} placeholder="Votre nom" size="large" maxLength={120} />
              </Form.Item>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Veuillez saisir votre email' },
                  { type: 'email', message: 'Email invalide' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Votre email" size="large" maxLength={254} />
              </Form.Item>
            </div>

            <Form.Item name="phone" label="Téléphone">
              <Input prefix={<PhoneOutlined />} placeholder="Votre numéro de téléphone" size="large" maxLength={40} />
            </Form.Item>

            <Form.Item
              name="subject"
              label="Sujet"
              rules={[{ required: true, message: 'Veuillez saisir un sujet' }]}
            >
              <Input placeholder="Sujet de votre message" size="large" maxLength={160} />
            </Form.Item>

            <Form.Item
              name="message"
              label="Message"
              rules={[{ required: true, message: 'Veuillez saisir votre message' }]}
            >
              <TextArea placeholder="Votre message..." rows={6} maxLength={5000} />
            </Form.Item>

            <Button htmlType="submit" size="large" block className="ct-submit" loading={sending}>
              {sending ? 'Envoi en cours…' : 'Envoyer le message'}
            </Button>
          </Form>
        </div>

        <aside className="ct-side">
          {/* Coordonnées : carte noire */}
          <div className="ct-card ct-dark">
            <h2>Nos coordonnées</h2>
            <ul className="ct-contacts">
              {COORDONNEES.map((c) => (
                <li key={c.label}>
                  <span className="ct-icon">{c.icon}</span>
                  <span>
                    <small>{c.label}</small>
                    {c.href ? <a href={c.href}>{c.texte}</a> : <address>{c.texte}</address>}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Horaires et support */}
          <div className="ct-card">
            <h3><ClockCircleOutlined /> Heures d'ouverture</h3>
            <ul className="ct-hours">
              {HORAIRES.map((h) => (
                <li key={h.jour}><span>{h.jour}</span><strong>{h.heures}</strong></li>
              ))}
            </ul>
            <h3 className="ct-support-title">Support technique</h3>
            <p>Notre équipe technique est disponible du lundi au vendredi de 08:00 à 20:00.</p>
            <a className="ct-mail" href="mailto:shopmercuri@gmail.com">shopmercuri@gmail.com</a>
          </div>
        </aside>
      </section>

      {/* FAQ rapide */}
      <section className="ct-faq">
        <h2>Questions fréquentes</h2>
        <div className="ct-faq-grid">
          {FAQ.map((item) => (
            <article key={item.question} className="ct-card">
              <span className="ct-icon">{item.icon}</span>
              <h3>{item.question}</h3>
              <p>{item.reponse}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Contact;
